import apiClient from './client';
import { API_BASE_URL, API_ENDPOINTS } from './config';
import type { BookingLinkTokens } from '../types/bookingLink';

const PUBLIC_BOOKING_PATH = '/book';

/**
 * Origin the portal itself is served from — and therefore where the public
 * booking pages live.
 *
 * `VITE_PORTAL_BASE_URL` pins it per environment (test./www. portal domains).
 * Left unset in local development it falls back to the origin the page is
 * already running on, so a generated link opens in the same browser whatever
 * port Vite bound to. Read live rather than at import time for that reason.
 * Setting it locally is still useful to pin a LAN address for phone testing.
 */
const getPortalOrigin = (): string => {
	const configured = import.meta.env.VITE_PORTAL_BASE_URL;
	if (configured) return configured;
	return typeof window !== 'undefined' ? window.location.origin : '';
};

const withToken = (origin: string, token: string): string =>
	`${origin.replace(/\/+$/, '')}${PUBLIC_BOOKING_PATH}?token=${encodeURIComponent(token)}`;

export const bookingLinkApi = {
	/**
	 * Get the public booking tokens for a location.
	 */
	generate: async (locationId: number): Promise<BookingLinkTokens> => {
		return apiClient.post<BookingLinkTokens>(
			`${API_ENDPOINTS.BOOKING_LINK.GENERATE}?locationId=${locationId}`
		);
	},
};

/**
 * Walk-in (no payment) booking link — served from the portal domain.
 */
export const buildWalkInBookingUrl = (token: string): string => withToken(getPortalOrigin(), token);

/**
 * Book-with-payment link.
 *
 * Still served from the API host on stage and production: that flow is not
 * finished on the portal yet, so its shared links must keep resolving to the
 * page that does work. Move it to `getPortalOrigin()` — i.e. make this the
 * same as the walk-in builder — once the paid flow ships.
 *
 * Local development always uses the portal origin regardless, so both links
 * point at the dev server and the paid page can be worked on end to end.
 */
export const buildPaidBookingUrl = (token: string): string =>
	withToken(import.meta.env.DEV ? getPortalOrigin() : API_BASE_URL || '', token);
